import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  GraduationCap, Plus, Upload, Trash2, Edit2, BarChart3, Server, X, CheckCircle2,
  XCircle, Loader2, ArrowLeft, Search, Video, Save, RefreshCw, FileDown, PieChart, ClipboardList
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AdminSurveysPanel from './AdminSurveysPanel';

const formatBytes = (bytes) => {
  if (!bytes) return '—';
  const mb = bytes / (1024 * 1024);
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const formatPercent = (value) => (value == null || Number.isNaN(value) ? '—' : `${value}%`);

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < (arr || []).length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
};

const calcPercent = (part, total) => {
  const done = part ?? 0;
  const all = total ?? 0;
  if (all <= 0) return null;
  return Math.round((done / all) * 100);
};

const getCompletionPercent = (stats) => {
  if (!stats) return null;
  if (stats.eligibleUsersAvailable && stats.eligibleCompletionPercent != null) {
    return { value: stats.eligibleCompletionPercent, scope: 'eligible' };
  }
  const loggedIn = stats.loggedInCompletionPercent
    ?? stats.portalCompletionPercent
    ?? calcPercent(stats.completed, stats.loggedInEligible ?? stats.totalUsers);
  if (loggedIn != null) return { value: loggedIn, scope: 'loggedIn' };
  return null;
};

const AUDIENCE_LABELS = {
  MANAGERIAL: 'Kierownicze',
  EMPLOYEE: 'Pracownicze',
};

const loggedInLabel = (audienceType) =>
  audienceType === 'MANAGERIAL' ? 'Zalogowanych kierowników' : 'Zalogowanych pracowników';

const eligibleLabel = (audienceType) =>
  audienceType === 'MANAGERIAL' ? 'Kierowników (łącznie)' : 'Pracowników (łącznie)';

const StatCell = ({ label, value, valueClass = 'text-gray-900' }) => (
  <div className="flex flex-col">
    <span className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">{label}</span>
    <span className={`text-lg font-bold leading-tight ${valueClass}`}>{value}</span>
  </div>
);

const TrainingStats = ({ stats }) => {
  const completion = getCompletionPercent(stats);
  const eligibleUnavailable = stats && stats.eligibleUsersAvailable === false;
  const eligibleTitle = eligibleUnavailable ? stats?.eligibleUsersMessage : undefined;
  const audience = stats?.audienceType;

  const total = stats?.eligibleUsersAvailable
    ? stats.eligibleUsers
    : (stats?.loggedInEligible ?? stats?.totalUsers ?? 0);
  const totalLabel = stats?.eligibleUsersAvailable ? eligibleLabel(audience) : loggedInLabel(audience);
  const percentValue = completion?.value ?? 0;

  const barColor =
    percentValue >= 80 ? 'bg-green-500' : percentValue >= 50 ? 'bg-indigo-500' : 'bg-amber-500';

  return (
    <div className="mt-3">
      <div className="grid grid-cols-3 gap-4 max-w-md">
        <StatCell label="Rozpoczęło" value={stats?.started ?? 0} valueClass="text-indigo-600" />
        <StatCell label="Ukończyło" value={stats?.completed ?? 0} valueClass="text-green-600" />
        <StatCell label={totalLabel} value={total} />
      </div>

      <div className="mt-3 max-w-md">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-500">
            Ukończenie
            {completion?.scope === 'loggedIn' ? ' (zalogowani)' : ''}
            {eligibleUnavailable && (
              <span className="text-amber-600 cursor-help ml-1" title={eligibleTitle}>ⓘ</span>
            )}
          </span>
          <span className="text-sm font-bold text-gray-900">{formatPercent(completion?.value)}</span>
        </div>
        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${barColor}`}
            style={{ width: `${Math.min(100, percentValue)}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// ---------- Modal dodawania/edycji szkolenia ----------
const TrainingFormModal = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [required, setRequired] = useState(initial?.required ?? true);
  const [active, setActive] = useState(initial?.active ?? true);
  const [audienceType, setAudienceType] = useState(initial?.audienceType || 'EMPLOYEE');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Tytuł jest wymagany');
      return;
    }
    try {
      setSaving(true);
      const payload = { title: title.trim(), description, required, active, audienceType };
      if (isEdit) {
        await api.put(`/admin/trainings/${initial.id}`, payload);
        toast.success('Zapisano zmiany');
      } else {
        await api.post('/admin/trainings', payload);
        toast.success('Dodano szkolenie');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Błąd zapisu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-bold text-gray-900">
            {isEdit ? 'Edytuj szkolenie' : 'Nowe szkolenie'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tytuł</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="np. Szkolenie BHP 2026"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Opis</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Krótki opis szkolenia"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Typ szkolenia</label>
            <select
              value={audienceType}
              onChange={(e) => setAudienceType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="EMPLOYEE">Pracownicze (widoczne poza grupą kierowniczą)</option>
              <option value="MANAGERIAL">Kierownicze (widoczne tylko w grupie AD kierowników)</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Kierownicze widzą tylko szkolenia kierownicze, pozostali — tylko pracownicze.
            </p>
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
              Obowiązkowe
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              Aktywne (widoczne dla pracowników)
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
              Anuluj
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Zapisywanie…' : 'Zapisz'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------- Raport szkolenia ----------
const TrainingReport = ({ trainingId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/admin/trainings/${trainingId}/report`)
      .then((res) => { if (mounted) setData(res.data); })
      .catch(() => toast.error('Nie udało się pobrać raportu'))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [trainingId]);

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Ładowanie raportu…</div>;
  }
  if (!data) return null;

  const rows = (data.rows || []).filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.displayName || '').toLowerCase().includes(s) || (r.username || '').toLowerCase().includes(s);
  });

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <ArrowLeft className="h-4 w-4" /> Powrót do szkoleń
      </button>

      <h2 className="text-xl font-bold text-gray-900">{data.training?.title}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 my-4 max-w-4xl">
        <div className="bg-white rounded-xl p-4 ring-1 ring-black/5">
          <p className="text-2xl font-bold text-gray-900">{data.summary?.eligibleUsersAvailable ? data.summary.eligibleUsers : '—'}</p>
          <p className="text-xs text-gray-500">{eligibleLabel(data.summary?.audienceType)} (dostęp do szkolenia)</p>
        </div>
        <div className="bg-white rounded-xl p-4 ring-1 ring-black/5">
          <p className="text-2xl font-bold text-gray-900">{data.summary?.loggedInEligible ?? data.summary?.totalUsers ?? 0}</p>
          <p className="text-xs text-gray-500">{loggedInLabel(data.summary?.audienceType)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 ring-1 ring-black/5">
          <p className="text-2xl font-bold text-indigo-600">{data.summary?.started ?? 0}</p>
          <p className="text-xs text-gray-500">rozpoczęło</p>
        </div>
        <div className="bg-white rounded-xl p-4 ring-1 ring-black/5">
          <p className="text-2xl font-bold text-green-600">{data.summary?.completed ?? 0}</p>
          <p className="text-xs text-gray-500">ukończyło</p>
        </div>
        <div className="bg-white rounded-xl p-4 ring-1 ring-black/5">
          <p className="text-2xl font-bold text-indigo-600">
            {formatPercent(getCompletionPercent(data.summary)?.value)}
          </p>
          <p className="text-xs text-gray-500">
            {getCompletionPercent(data.summary)?.scope === 'eligible' ? 'ukończenie (łącznie)' : 'ukończenie (zalog.)'}
          </p>
        </div>
      </div>
      {!data.summary?.eligibleUsersAvailable && data.summary?.eligibleUsersMessage && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-4 max-w-4xl">
          {data.summary.eligibleUsersMessage}
        </p>
      )}

      <div className="relative max-w-xs mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Szukaj pracownika…"
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div className="bg-white rounded-xl ring-1 ring-black/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left font-medium px-4 py-2.5">Pracownik</th>
              <th className="text-left font-medium px-4 py-2.5">Login</th>
              <th className="text-left font-medium px-4 py-2.5">Postęp</th>
              <th className="text-left font-medium px-4 py-2.5">Status</th>
              <th className="text-left font-medium px-4 py-2.5">Ostatnio oglądał</th>
              <th className="text-left font-medium px-4 py-2.5">Ukończono</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Brak danych</td></tr>
            ) : rows.map((r) => (
              <tr key={r.username} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 text-gray-900">{r.displayName}</td>
                <td className="px-4 py-2.5 text-gray-500">{r.username}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-24 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full ${r.completed ? 'bg-green-500' : 'bg-indigo-500'}`} style={{ width: `${Math.min(100, r.percentWatched || 0)}%` }} />
                    </div>
                    <span className="text-xs text-gray-500">{r.percentWatched || 0}%</span>
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  {r.completed ? (
                    <span className="inline-flex items-center gap-1 text-green-600 text-xs font-semibold"><CheckCircle2 className="h-4 w-4" /> Ukończone</span>
                  ) : r.started ? (
                    <span className="text-amber-600 text-xs font-semibold">W trakcie</span>
                  ) : (
                    <span className="text-gray-400 text-xs">Nierozpoczęte</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-gray-500 text-xs">{formatDate(r.lastViewedAt)}</td>
                <td className="px-4 py-2.5 text-gray-500 text-xs">{formatDate(r.completedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Konfiguracja Active Directory ----------
const AdConfigPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [bindPassword, setBindPassword] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/ad-config')
      .then((res) => setCfg(res.data))
      .catch(() => toast.error('Nie udało się pobrać konfiguracji AD'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => setCfg((prev) => ({ ...prev, ...patch }));

  const buildPayload = () => {
    const payload = { ...cfg };
    delete payload.bindPasswordSet;
    if (bindPassword) payload.bindPassword = bindPassword;
    return payload;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await api.put('/admin/ad-config', buildPayload());
      setCfg(res.data);
      setBindPassword('');
      toast.success('Zapisano konfigurację AD');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Błąd zapisu');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      const res = await api.post('/admin/ad-config/test', buildPayload());
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.error || 'Błąd testu połączenia' });
    } finally {
      setTesting(false);
    }
  };

  if (loading || !cfg) {
    return <div className="flex items-center justify-center py-16"><LoadingSpinner size="lg" /></div>;
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-lg mb-5">
        <Server className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          Skonfiguruj połączenie z kontrolerem domeny Active Directory. Pracownicy będą logować się do
          portalu szkoleń swoimi <strong>kontami domenowymi</strong>.
        </p>
      </div>

      <form className="bg-white rounded-xl ring-1 ring-black/5 p-6 space-y-4" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        {/* Pola-pułapki neutralizujące autouzupełnianie przeglądarki (CRM login/hasło) */}
        <input type="text" name="fakeusernameremembered" autoComplete="username" className="hidden" tabIndex={-1} aria-hidden="true" />
        <input type="password" name="fakepasswordremembered" autoComplete="new-password" className="hidden" tabIndex={-1} aria-hidden="true" />
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={!!cfg.enabled}
            onChange={(e) => update({ enabled: e.target.checked })}
            className="h-4 w-4"
          />
          <span className="text-sm font-semibold text-gray-800">Logowanie przez Active Directory włączone</span>
        </label>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Serwer (host)</label>
            <input value={cfg.host || ''} onChange={(e) => update({ host: e.target.value })}
              placeholder="dc01.szpitallapy.local"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
            <input type="number" value={cfg.port || 389} onChange={(e) => update({ port: parseInt(e.target.value, 10) || 389 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
              <input type="checkbox" checked={!!cfg.useSsl} onChange={(e) => update({ useSsl: e.target.checked })} />
              Połączenie SSL (LDAPS)
            </label>
          </div>
        </div>

        <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-100 rounded-lg text-xs text-emerald-800">
          <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            <strong>Zostaw SSL wyłączone</strong> — logowanie odbywa się przez <strong>Kerberos</strong> (jak w Windows),
            działa bez certyfikatu i bez zmian na kontrolerze domeny. Port jest wtedy nieistotny.
            Włącz SSL tylko, jeśli DC ma skonfigurowany certyfikat LDAPS (port 636).
          </span>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Domena</label>
          <input value={cfg.domain || ''} onChange={(e) => update({ domain: e.target.value })}
            placeholder="szpitallapy.local"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          <p className="text-xs text-gray-400 mt-1">Używana do budowy loginu (login@domena).</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Baza wyszukiwania (Base DN)</label>
          <input value={cfg.baseDn || ''} onChange={(e) => update({ baseDn: e.target.value })}
            placeholder="DC=szpitallapy,DC=local"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Filtr użytkownika (opcjonalnie)</label>
          <input value={cfg.userSearchFilter || ''} onChange={(e) => update({ userSearchFilter: e.target.value })}
            placeholder="(sAMAccountName={0})"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          <p className="text-xs text-gray-400 mt-1">Domyślnie: (|(sAMAccountName={'{0}'})(userPrincipalName={'{0}'})).</p>
        </div>

        <div className="border-t pt-4">
          <p className="text-sm font-semibold text-gray-700 mb-2">Konto serwisowe (wymagane do statystyk domeny)</p>
          <p className="text-xs text-gray-500 mb-3">
            Konto serwisowe służy do sprawdzania członkostwa w grupie AD przy logowaniu.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bind DN</label>
              <input value={cfg.bindDn || ''} onChange={(e) => update({ bindDn: e.target.value })}
                name="ad-bind-dn"
                autoComplete="off"
                readOnly
                onFocus={(e) => e.target.removeAttribute('readonly')}
                placeholder="serwis@szpital2.local"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hasło {cfg.bindPasswordSet && <span className="text-xs text-green-600">(ustawione)</span>}
              </label>
              <input type="password" value={bindPassword} onChange={(e) => setBindPassword(e.target.value)}
                name="ad-bind-secret"
                autoComplete="new-password"
                readOnly
                onFocus={(e) => e.target.removeAttribute('readonly')}
                placeholder={cfg.bindPasswordSet ? '••••••• (bez zmian)' : 'Hasło konta serwisowego'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Grupa kierownicza w AD</label>
          <input value={cfg.managerGroupName || ''} onChange={(e) => update({ managerGroupName: e.target.value })}
            placeholder="Szkolenia kierownicze"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          <p className="text-xs text-gray-400 mt-1">
            Nazwa grupy bezpieczeństwa w Active Directory. Członkowie widzą szkolenia kierownicze, pozostali — pracownicze.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hostname kontrolera domeny (LDAP)</label>
          <input value={cfg.ldapHostname || ''} onChange={(e) => update({ ldapHostname: e.target.value })}
            placeholder="DC1.szpital2.local"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          <p className="text-xs text-gray-400 mt-1">
            Wymagane do zapytań o grupy przy logowaniu (GSSAPI). Zostaw puste — system wykryje automatycznie.
          </p>
        </div>

        <div className="border-t pt-4">
          <p className="text-sm font-semibold text-gray-700 mb-2">Statystyki ukończenia szkoleń</p>
          <p className="text-xs text-gray-500 mb-3">
            Wpisz łączną liczbę pracowników i kierowników, którzy powinni ukończyć szkolenia.
            Procent ukończenia jest liczony jako: ukończyło ÷ wpisana liczba.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Liczba pracowników</label>
              <input
                type="number"
                min="1"
                value={cfg.manualEmployeeCount ?? ''}
                onChange={(e) => update({ manualEmployeeCount: e.target.value === '' ? null : parseInt(e.target.value, 10) || null })}
                placeholder="np. 908"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-400 mt-1">Mianownik procentu dla szkoleń pracowniczych.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Liczba kierowników</label>
              <input
                type="number"
                min="1"
                value={cfg.manualManagerCount ?? ''}
                onChange={(e) => update({ manualManagerCount: e.target.value === '' ? null : parseInt(e.target.value, 10) || null })}
                placeholder="np. 40"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-400 mt-1">Mianownik procentu dla szkoleń kierowniczych.</p>
            </div>
          </div>
        </div>

        {testResult && (
          <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {testResult.success ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
            {testResult.message}
          </div>
        )}

        <div className="flex justify-between pt-2">
          <button type="button" onClick={handleTest} disabled={testing}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Testuj połączenie
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? 'Zapisywanie…' : 'Zapisz'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ---------- Wykres kołowy (donut) ukończenia ----------
const DonutChart = ({ percent, color }) => {
  const size = 120;
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const value = Math.max(0, Math.min(100, percent || 0));
  const offset = circumference - (value / 100) * circumference;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#e5e7eb"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        fontSize="24"
        fontWeight="700"
        fill="#111827"
      >
        {value}%
      </text>
    </svg>
  );
};

// ---------- Zbiorczy raport graficzny (z eksportem PDF) ----------
const SummaryReport = ({ onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get('/admin/trainings/report/summary')
      .then((res) => { if (mounted) setData(res.data); })
      .catch(() => toast.error('Nie udało się pobrać raportu'))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const handleExport = async () => {
    if (!reportRef.current) return;
    try {
      setExporting(true);
      const html2pdf = (await import('html2pdf.js')).default;
      const dateStr = new Date().toISOString().slice(0, 10);
      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: `raport-szkolen-${dateStr}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'], avoid: '.pdf-user-row' },
        })
        .from(reportRef.current)
        .save();
    } catch (err) {
      toast.error('Nie udało się wygenerować PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Ładowanie raportu…</div>;
  }
  if (!data) return null;

  const modules = data.modules || [];
  const generatedAt = data.generatedAt ? new Date(data.generatedAt) : new Date();

  const colorFor = (audienceType) => (audienceType === 'MANAGERIAL' ? '#8b5cf6' : '#3b82f6');

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" /> Powrót do szkoleń
        </button>
        <button onClick={handleExport} disabled={exporting}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
          Pobierz PDF
        </button>
      </div>

      {/* Obszar renderowany do PDF */}
      <div ref={reportRef} className="bg-white rounded-xl ring-1 ring-black/5 p-8" style={{ color: '#111827' }}>
        <div className="flex items-center justify-between border-b border-gray-200 pb-5 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Raport ukończenia szkoleń</h1>
            <p className="text-sm text-gray-500 mt-1">Szpital Łapy — postęp obowiązkowych szkoleń</p>
          </div>
          <div className="text-right text-xs text-gray-500">
            Wygenerowano:<br />
            <span className="font-semibold text-gray-700">
              {generatedAt.toLocaleString('pl-PL', { dateStyle: 'long', timeStyle: 'short' })}
            </span>
          </div>
        </div>

        {modules.length === 0 ? (
          <p className="text-center text-gray-400 py-10">Brak szkoleń do raportu.</p>
        ) : (
          <div className="space-y-8">
            {modules.map((m) => (
              <div key={m.id} className="rounded-xl border border-gray-200">
                <div className="flex items-center gap-6 p-5 bg-gray-50 rounded-t-xl" style={{ pageBreakInside: 'avoid' }}>
                  <div className="shrink-0">
                    <DonutChart percent={m.completionPercent} color={colorFor(m.audienceType)} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                        m.audienceType === 'MANAGERIAL' ? 'text-purple-700 bg-purple-100' : 'text-blue-700 bg-blue-100'
                      }`}>
                        {AUDIENCE_LABELS[m.audienceType] || 'Pracownicze'}
                      </span>
                      {m.required && <span className="text-[11px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Obowiązkowe</span>}
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 mt-1.5">{m.title}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Ukończenie szkolenia: <strong className="text-gray-800">{m.completionPercent}%</strong>
                    </p>
                  </div>
                </div>

                <div className="p-5">
                  <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-3">
                    Pracownicy, którzy ukończyli ten moduł
                  </p>
                  {(m.completedUsers || []).length === 0 ? (
                    <p className="text-sm text-gray-400">Nikt jeszcze nie ukończył tego modułu.</p>
                  ) : (
                    <div>
                      {chunk(m.completedUsers, 3).map((group, gi) => (
                        <div
                          key={gi}
                          className="pdf-user-row flex"
                          style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}
                        >
                          {group.map((u) => (
                            <div key={u.username} className="flex items-start gap-2 text-sm text-gray-700 py-1.5 pr-4" style={{ width: '33.333%' }}>
                              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                              <span className="break-words" style={{ lineHeight: 1.5 }}>{u.displayName}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Główny komponent ----------
const AdminTrainings = () => {
  const [tab, setTab] = useState('trainings'); // 'trainings' | 'surveys' | 'ad'
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {} | training
  const [reportId, setReportId] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const fileInputRef = useRef(null);
  const pendingUploadId = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/trainings')
      .then((res) => setTrainings(res.data || []))
      .catch(() => toast.error('Nie udało się pobrać szkoleń'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (t) => {
    if (!window.confirm(`Usunąć szkolenie "${t.title}"? Tej operacji nie można cofnąć.`)) return;
    try {
      await api.delete(`/admin/trainings/${t.id}`);
      toast.success('Usunięto szkolenie');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Błąd usuwania');
    }
  };

  const handleDeleteVideo = async (t) => {
    if (!window.confirm(`Usunąć film ze szkolenia "${t.title}"?\n\nSzkolenie zostanie — możesz potem wgrać nowy plik.`)) return;
    try {
      await api.delete(`/admin/trainings/${t.id}/video`);
      toast.success('Film usunięty');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Błąd usuwania filmu');
    }
  };

  const triggerUpload = (id) => {
    pendingUploadId.current = id;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const id = pendingUploadId.current;
    if (!file || !id) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploadingId(id);
      setUploadPercent(0);
      await api.post(`/admin/trainings/${id}/video`, formData, {
        timeout: 1800000, // 30 min na duże pliki
        onUploadProgress: (evt) => {
          if (evt.total) setUploadPercent(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      toast.success('Film został wgrany');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Błąd wgrywania filmu');
    } finally {
      setUploadingId(null);
      setUploadPercent(0);
      pendingUploadId.current = null;
    }
  };

  if (reportId) {
    return (
      <div className="p-6">
        <TrainingReport trainingId={reportId} onBack={() => setReportId(null)} />
      </div>
    );
  }

  if (showSummary) {
    return (
      <div className="p-6">
        <SummaryReport onBack={() => setShowSummary(false)} />
      </div>
    );
  }

  return (
    <div className="p-6">
      <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={handleFileSelected} />

      <div className="flex items-center gap-3 mb-1">
        <div className="h-10 w-10 rounded-lg bg-indigo-600 flex items-center justify-center">
          <GraduationCap className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Szkolenia / Ankiety</h1>
          <p className="text-sm text-gray-500">Zarządzanie filmami szkoleniowymi, ankietami i konfiguracją domeny</p>
        </div>
      </div>

      {/* Zakładki */}
      <div className="flex gap-1 border-b border-gray-200 mt-5 mb-6">
        <button
          onClick={() => setTab('trainings')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            tab === 'trainings' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="inline-flex items-center gap-2"><Video className="h-4 w-4" /> Szkolenia</span>
        </button>
        <button
          onClick={() => setTab('surveys')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            tab === 'surveys' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="inline-flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Ankiety</span>
        </button>
        <button
          onClick={() => setTab('ad')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            tab === 'ad' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="inline-flex items-center gap-2"><Server className="h-4 w-4" /> Active Directory</span>
        </button>
      </div>

      {tab === 'ad' ? (
        <AdConfigPanel />
      ) : tab === 'surveys' ? (
        <AdminSurveysPanel />
      ) : (
        <>
          <div className="flex justify-end gap-2 mb-4">
            <button
              onClick={() => setShowSummary(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100"
            >
              <PieChart className="h-4 w-4" /> Generuj raport
            </button>
            <button
              onClick={() => setModal({})}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              <Plus className="h-4 w-4" /> Dodaj szkolenie
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16"><LoadingSpinner size="lg" /></div>
          ) : trainings.length === 0 ? (
            <div className="bg-white rounded-xl ring-1 ring-black/5 p-10 text-center text-gray-500">
              <GraduationCap className="h-10 w-10 mx-auto text-gray-300 mb-3" />
              Brak szkoleń. Dodaj pierwsze szkolenie i wgraj film.
            </div>
          ) : (
            <div className="space-y-4">
              {trainings.map((t) => {
                const isManagerial = t.audienceType === 'MANAGERIAL';
                return (
                <div key={t.id} className="bg-white rounded-2xl ring-1 ring-black/5 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                  <div className={`h-1 w-full ${isManagerial ? 'bg-purple-400' : 'bg-blue-400'}`} />
                  <div className="p-5">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-5">
                      {/* Lewa kolumna: tytuł, znaczniki, statystyki */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-3">
                          <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${
                            isManagerial ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'
                          }`}>
                            <Video className="h-5 w-5" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900 text-base leading-tight truncate">{t.title}</h3>
                            <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                                isManagerial ? 'text-purple-700 bg-purple-100' : 'text-blue-700 bg-blue-100'
                              }`}>
                                {AUDIENCE_LABELS[t.audienceType] || 'Pracownicze'}
                              </span>
                              {t.required && <span className="text-[11px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Obowiązkowe</span>}
                              {!t.active && <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Nieaktywne</span>}
                              {t.hasVideo ? (
                                <span className="text-[11px] font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                                  Film · {formatBytes(t.videoSizeBytes)}
                                </span>
                              ) : (
                                <span className="text-[11px] font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-full">Brak filmu</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {t.description && <p className="text-sm text-gray-500 mt-3 line-clamp-1">{t.description}</p>}
                        <TrainingStats stats={t.stats} />
                        {uploadingId === t.id && (
                          <div className="mt-3 max-w-md">
                            <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-indigo-500 transition-all" style={{ width: `${uploadPercent}%` }} />
                            </div>
                            <p className="text-xs text-gray-400 mt-1">Wgrywanie… {uploadPercent}%</p>
                          </div>
                        )}
                      </div>

                      {/* Prawa kolumna: akcje */}
                      <div className="flex items-center gap-2 lg:flex-col lg:items-stretch lg:w-44 shrink-0 flex-wrap border-t lg:border-t-0 lg:border-l border-gray-100 pt-4 lg:pt-0 lg:pl-5">
                        <button onClick={() => setReportId(t.id)} title="Raport"
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 flex-1 lg:flex-none">
                          <BarChart3 className="h-4 w-4" /> Raport
                        </button>
                        <button onClick={() => triggerUpload(t.id)} disabled={uploadingId === t.id}
                          title="Wgraj film"
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100 disabled:opacity-50 flex-1 lg:flex-none">
                          <Upload className="h-4 w-4" /> {t.hasVideo ? 'Zmień film' : 'Wgraj film'}
                        </button>
                        <div className="flex items-center gap-2 lg:mt-1">
                          {t.hasVideo && (
                            <button onClick={() => handleDeleteVideo(t)} disabled={uploadingId === t.id}
                              title="Usuń film (zostaw szkolenie)"
                              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-red-700 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 flex-1">
                              <Trash2 className="h-4 w-4" /> Film
                            </button>
                          )}
                          <button onClick={() => setModal(t)} title="Edytuj szkolenie"
                            className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg ring-1 ring-gray-200">
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDelete(t)} title="Usuń szkolenie"
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg ring-1 ring-gray-200">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {modal && (
        <TrainingFormModal
          initial={modal}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default AdminTrainings;
